import React, { useContext, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import groupBy from 'lodash/groupBy'
import {
  ClientOnList,
  ClientListManagerContext,
  Order,
} from '~/service/ClientListManager'
import trans from '~/translations/trans'
import { FormattedMessage } from '~/translations/utils'
import MainContent from '../MainContent'
import { FilterButton } from '../common/List'
import { CampaignManagerContext } from '~/service/CampaignManager'
import DateTooltip from '~/components/common/DateTooltip'
import Star, { stars } from '~/components/common/Star'
import Search from '~/components/common/Search'
import useResizeObserver from 'use-resize-observer'
import AddClient from './AddClient'
import TableHeader from './list/TableHeader'
import { formatDate, formatRelative } from '~/utils/dateFns'

const groupByDate = (clients: ClientOnList[]) =>
  Object.values(
    groupBy(clients, client => formatDate(new Date(client.added), 'P'))
  )

const List: React.FC = ({}) => {
  const {
    loadHook,
    clients,
    search,
    setSearch,
    loadMore,
    loading,
    filter,
    setFilter,
    order,
  } = useContext(ClientListManagerContext)
  const { campaigns } = useContext(CampaignManagerContext)

  const { ref, width = 0 } = useResizeObserver<HTMLElement>()

  loadHook()

  const groupsStarred = useMemo(
    () => groupByDate(clients.filter(item => item.starred)),
    [clients]
  )
  const groupsUnstarred = useMemo(
    () => groupByDate(clients.filter(item => !item.starred)),
    [clients]
  )

  const filterButtonCommon = { filter, setFilter }

  return (
    <>
      <div className="main-header list-header" ref={ref}>
        <Search
          filter={filter}
          setFilter={setFilter}
          search={search}
          setSearch={setSearch}
          small={width > 800}
        />

        <div className="client-list-header-add">
          <AddClient />
        </div>

        <div className="list-filter">
          {filter['tmCampaign'] && (
            <FilterButton
              {...filterButtonCommon}
              name="tmCampaign"
              label={
                campaigns.find(c => c.id == filter['tmCampaign'])
                  ?.name as string
              }
              removable
            />
          )}
        </div>
      </div>
      <div className="main-columns-item">
        <div className="list-table-wrapper-header">
          <TableHeader />
        </div>
        <MainContent onScrolledToEnd={() => loadMore()}>
          <div className="list client-list">
            {groupsStarred.length || groupsUnstarred.length ? (
              <>
                {groupsStarred.length > 0 && (
                  <>
                    <div className="list-items-header client-list-items-header client-list-items-header-star is-center">
                      <span className="fa fa-star" />
                      <FormattedMessage id="clients / starred" />
                    </div>
                    {order == 'addedAsc' ? (
                      groupsStarred.map((clients, k) => (
                        <Items clients={clients} order={order} key={k} />
                      ))
                    ) : (
                      <Items
                        clients={clients.filter(item => item.starred)}
                        order={order}
                      />
                    )}
                  </>
                )}
                {groupsStarred.length > 0 && groupsUnstarred.length > 0 && (
                  <div className="list-items-header client-list-items-header client-list-items-header-star is-center">
                    <span className="far fa-star" />
                    <FormattedMessage id="clients / rest" />
                  </div>
                )}
                {order == 'addedAsc' ? (
                  groupsUnstarred.map((clients, k) => (
                    <Items
                      clients={clients.filter(item => !item.starred)}
                      order={order}
                      key={k}
                    />
                  ))
                ) : (
                  <Items
                    clients={clients.filter(item => !item.starred)}
                    order={order}
                  />
                )}
              </>
            ) : (
              !loading && (
                <div className="list-empty">
                  <FormattedMessage id="clients / empty" />
                </div>
              )
            )}

            <div
              className={classNames('list-loading', {
                'is-active': loading,
              })}
            >
              <span className="fa fa-spin fa-spinner-third" />
            </div>
          </div>
        </MainContent>
      </div>
    </>
  )
}

type ItemsProps = {
  clients: ClientOnList[]
  order: Order
}

const Items: React.FC<ItemsProps> = ({ clients, order }) => {
  return (
    <>
      {order == 'addedAsc' && (
        <div className="list-items-header client-list-items-header">
          {formatRelative(new Date(clients[0].added))}
        </div>
      )}

      <div className="list-items client-list-items">
        {clients.map(client => (
          <Item client={client} key={client.id} />
        ))}
      </div>
    </>
  )
}

type ItemProps = {
  client: ClientOnList
}

const Item: React.FC<ItemProps> = ({ client }) => {
  const star =
    client.stars && client.stars.length && client.stars[0].colorId != 0
      ? { color: stars[client.stars[0].colorId] }
      : null

  return (
    <div className="list-item client-list-item">
      <div className="client-list-item-star">
        <Star type="client" star={star} id={client.id} />
      </div>
      <Link to={'/clients/' + client.id} className="client-list-item-avatar">
        <img src={client.avatar} />
      </Link>
      <Link to={'/clients/' + client.id} className="client-list-item-client">
        {client.name}
      </Link>
      <Link to={'/clients/' + client.id} className="client-list-item-tag">
        {client.tags.map(tag => '#' + tag.name + ' ')}
      </Link>
      <Link to={'/clients/' + client.id} className="client-list-item-date">
        <DateTooltip date={client.added} />
      </Link>
    </div>
  )
}

export default List
